/* ============================================================
   SR CART — SRPROFUMO on Horizon 4.1.5

   Several SRPROFUMO components add to cart themselves (routine builder,
   recently-viewed rail, search modal, cart rewards) instead of going through
   <product-form-component>. On Horizon 3.5.1 they announced that by dispatching
   a plain `cart:update` CustomEvent, which the old cart-items component read.

   Horizon 4.1.5 replaced that with the Shopify Standard Event
   `shopify:cart:lines-update`, and the contract is no longer a plain event:

     - `event.action`  — 'add' | 'change' | 'clear'; the cart drawer only
                         auto-opens for 'add'
     - `event.promise` — a promise resolving to `{ detail: { sections, ... } }`.
                         component-cart-items.js awaits it and morphs the cart
                         section from `detail.sections[sectionId]`
     - `event.target`  — used by cart-drawer.js to detect a source <dialog>

   This helper builds that shape once so each SR component does not have to.
   It is a classic script (no import map), exposed as `window.SRCart`.
   ============================================================ */
(function () {
  'use strict';
  if (window.SRCart) return;

  var EVENT = 'shopify:cart:lines-update';

  /** Section ids the cart renders into, so the response can morph them. */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /* Theme.routes.cart_add_url already carries the .js suffix; the others do not. */
  function cartRoutes() {
    var routes = (window.Theme && window.Theme.routes) || {};
    return {
      add: routes.cart_add_url || '/cart/add.js',
      update: (routes.cart_update_url || '/cart/update') + '.js',
      change: (routes.cart_change_url || '/cart/change') + '.js'
    };
  }

  /**
   * Dispatch the Standard Event. Returns the deferred so the caller can settle
   * it once the cart response is in.
   * @param {'add'|'change'|'clear'} action
   * @param {Array<{merchandiseId: string, quantity: number}>} lines
   * @param {EventTarget} [source] - element the event is dispatched from
   */
  function announce(action, lines, source) {
    var resolve, reject;
    var promise = new Promise(function (res, rej) { resolve = res; reject = rej; });

    var event = new CustomEvent(EVENT, { bubbles: true, composed: true });
    event.action = action;
    event.context = 'product';
    event.lines = lines || [];
    event.promise = promise;

    (source || document).dispatchEvent(event);
    return { resolve: resolve, reject: reject };
  }

  /** Re-fetch the cart plus the section markup needed to morph it. */
  function fetchCartAndSections() {
    var ids = cartSectionIds();
    var url = '/cart.js';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!ids.length) return { cart: cart, sections: {} };
        return fetch('/?sections=' + encodeURIComponent(ids.join(',')), {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.json(); })
          .then(function (sections) { return { cart: cart, sections: sections }; })
          .catch(function () { return { cart: cart, sections: {} }; });
      });
  }

  /**
   * Add one or more lines to the cart and drive the native cart UI.
   * @param {Array<{id: string|number, quantity?: number, properties?: object}>} items
   * @param {object} [options]
   * @param {EventTarget} [options.source] - element to dispatch from
   * @param {string} [options.sourceId] - identifier surfaced in the event detail
   * @returns {Promise<object>} the /cart/add.js response
   */
  function add(items, options) {
    options = options || {};
    var list = Array.isArray(items) ? items : [items];
    var lines = list.map(function (it) {
      return { merchandiseId: String(it.id), quantity: Number(it.quantity) || 1 };
    });

    var deferred = announce('add', lines, options.source);
    var sectionIds = cartSectionIds();

    var body = { items: list };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    return fetch(cartRoutes().add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (result) {
        var didError = !result.ok || !!result.data.status;

        return fetchCartAndSections().then(function (state) {
          deferred.resolve({
            cart: state.cart,
            detail: {
              items: state.cart.items,
              source: 'sr-cart',
              sourceId: options.sourceId || 'sr-cart',
              sections: result.data.sections || state.sections,
              didError: didError
            }
          });
          if (didError) throw result.data;
          return result.data;
        });
      })
      .catch(function (error) {
        deferred.reject(error);
        throw error;
      });
  }

  /** Announce that the cart changed elsewhere, so the native UI re-renders. */
  function refresh(options) {
    options = options || {};
    var deferred = announce(options.action || 'change', [], options.source);
    return fetchCartAndSections()
      .then(function (state) {
        deferred.resolve({
          cart: state.cart,
          detail: {
            items: state.cart.items,
            source: 'sr-cart',
            sourceId: options.sourceId || 'sr-cart',
            sections: state.sections,
            didError: false
          }
        });
        return state.cart;
      })
      .catch(function (error) {
        deferred.reject(error);
        throw error;
      });
  }

  /* Open the native cart drawer. In 4.1.5 the markup nests as
     <theme-drawer id="cart-drawer"> > <dialog> > <cart-drawer-component>,
     so the element carrying open() is the outer theme-drawer. */
  function openDrawer() {
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) {
      var inner = document.querySelector('cart-drawer-component');
      drawer = inner && inner.closest('theme-drawer');
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  window.SRCart = {
    EVENT: EVENT,
    add: add,
    refresh: refresh,
    openDrawer: openDrawer,
    cartSectionIds: cartSectionIds
  };
})();
